import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Form } from "react-final-form";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import UiButton from "../../../components/button/Button";
import UiFieldMessage from "../../../components/field-message/FieldMessage.component";
import UiSelect from "../../../components/select/Select.component";
import UiInputDate from "../../../components/input-date/InputDate.component";
import FormSection from "../../../components/form-section/FormSection.component";
import { FieldMessageSeverity } from "../../../models/components/field-message.model";
import { Validations } from "../../../services/form/validations.service";
import { ListOption } from "../../../models/misc.model";
import {
	CreateSentMessageRequest,
	SentMessageSource,
	SENT_MESSAGE_SOURCE_TO_CHANNEL,
	HEALTH_NETWORK_ID,
} from "../../../models/endpoints/sent-emails-endpoint.model";
import { ParticipantContextProps } from "../../../models/contexts/participant-context.model";
import { ParticipantContext } from "../../../contexts/Participant.context";
import { Formatter } from "../../../services/formatter/formatter.service";
import { useIsParticipantSyncableToMetriport } from "../../../hooks/useIsParticipantSyncableToMetriport";

interface MarkHsrAsSentFormData {
	recipient: string | null;
	date: Date;
	source: SentMessageSource | null;
}

const sourceOptions: ListOption[] = [
	new ListOption({ id: SentMessageSource.EMAIL, label: "Email" }),
	new ListOption({ id: SentMessageSource.FAX, label: "Fax" }),
	new ListOption({ id: SentMessageSource.EPIC, label: "Epic" }),
];

const BiometricsMarkHsrAsSent = ({
	isVisible,
	onComplete,
	onClose,
}: {
	isVisible: boolean;
	onComplete: () => void;
	onClose: () => void;
}) => {
	const { t } = useTranslation("common");
	const queryClient = useQueryClient();
	const urlParams = useParams<{ participantId: string }>();
	const participantContext = useContext<ParticipantContextProps>(ParticipantContext);
	const isParticipantSyncableToMetriport = useIsParticipantSyncableToMetriport();

	const [formData, setFormData] = useState<MarkHsrAsSentFormData>({
		recipient: null,
		date: new Date(),
		source: null,
	});
	const [showError, setShowError] = useState<boolean>(false);
	const [submitting, setSubmitting] = useState<boolean>(false);

	const recipientOptions = (
		participantContext.participant?.physicians
			?.filter((physician) => physician.email && physician.sendHealthSummaryReport)
			?.map(
				(physician) =>
					new ListOption({
						id: physician.email!,
						label: physician.name + " - " + physician.email!,
					})
			) ?? []
	)
		.concat(
			participantContext.participant?.physicians
				?.filter((physician) => physician.fax && physician.sendHealthSummaryReport)
				?.map(
					(physician) =>
						new ListOption({
							id: physician.fax!,
							label: physician.name + " - " + physician.fax!,
						})
				) ?? []
		)
		.concat(
			participantContext.participant?.caregivers
				?.filter((caregiver) => caregiver.email && caregiver.sendHealthSummaryReport)
				?.map(
					(caregiver) =>
						new ListOption({
							id: caregiver.email!,
							label: caregiver.name + " - " + caregiver.email!,
						})
				) ?? []
		)
		.concat(
			participantContext.participant?.emails
				?.filter((email) => email.emailAddress)
				?.map(
					(email) =>
						new ListOption({
							id: email.emailAddress!,
							label:
								participantContext.participant?.firstName +
								" " +
								participantContext.participant?.lastName +
								" - " +
								email.emailAddress,
						})
				) ?? []
		)
		.concat(
			isParticipantSyncableToMetriport
				? [
						new ListOption({
							id: HEALTH_NETWORK_ID,
							label: t("PARTICIPANT.BIOMETRICS.REPORT.HEALTH_NETWORKS"),
						}),
					]
				: []
		);

	const handleSubmit = async () => {
		const isHealthNetwork = formData.recipient === HEALTH_NETWORK_ID;
		if (!formData.recipient || (!formData.source && !isHealthNetwork)) return;

		setShowError(false);
		setSubmitting(true);

		const participantId = Number(urlParams.participantId);
		const channel = isHealthNetwork
			? HEALTH_NETWORK_ID
			: SENT_MESSAGE_SOURCE_TO_CHANNEL[formData.source!];

		const request: CreateSentMessageRequest = {
			body: {
				participantId,
				kannactId: participantContext.participant!.kannactId,
				timestamp: formData.date!.toISOString(),
				from: "Manual entry",
				source: formData.source,
				channel,
				to: formData.recipient,
				status: "SENT",
				origin: "HEALTH_REPORT",
				subject: "Health summary report",
			},
		};

		await EndpointsService.dataRetriever
			.createSentMessage(request)
			.then(() => {
				queryClient.invalidateQueries({
					queryKey: ["sent-reports", { participantId }],
				});
				queryClient.invalidateQueries({
					queryKey: ["sent-reports-count", { participantId }],
				});
				onComplete();
			})
			.catch(() => setShowError(true))
			.finally(() => setSubmitting(false));
	};

	const handleClose = () => {
		setFormData({ recipient: null, date: new Date(), source: null });
		setShowError(false);
		onClose();
	};

	return (
		<Dialog
			header={t("PARTICIPANT.BIOMETRICS.SENT_REPORTS.MARK_AS_SENT.TITLE")}
			visible={isVisible}
			className="biometrics-modal"
			breakpoints={{ "960px": "80vw", "641px": "100vw" }}
			style={{ width: "50vw" }}
			modal={true}
			focusOnShow={false}
			onHide={handleClose}
		>
			<Form
				onSubmit={handleSubmit}
				initialValues={formData}
				render={({ handleSubmit }) => (
					<form onSubmit={handleSubmit}>
						<FormSection>
							<div className="col-12">
								<UiSelect
									id="recipient"
									label="PARTICIPANT.BIOMETRICS.SENT_REPORTS.MARK_AS_SENT.RECIPIENT"
									name="recipient"
									onChange={(e) => {
										const newRecipient = e.target.value;
										setFormData({
											...formData,
											recipient: newRecipient,
											source:
												newRecipient === HEALTH_NETWORK_ID
													? null
													: formData.source,
										});
									}}
									validations={[Validations.required]}
									disabled={submitting}
									options={recipientOptions}
								/>
							</div>

							<div className="col-12 col-sm-6 col-md-4">
								<UiInputDate
									id="date"
									name="date"
									label="PARTICIPANT.BIOMETRICS.SENT_REPORTS.MARK_AS_SENT.DATE"
									onChange={(e) =>
										setFormData({
											...formData,
											date: (e.target.value as Date) ?? new Date(),
										})
									}
									showTime={true}
									disabled={submitting}
									selectionMode="single"
									maxDate={new Date()}
								/>
							</div>

							{formData.recipient !== HEALTH_NETWORK_ID && (
								<div className="col-12">
									<UiSelect
										id="source"
										label="PARTICIPANT.BIOMETRICS.SENT_REPORTS.MARK_AS_SENT.SOURCE"
										name="source"
										onChange={(e) =>
											setFormData({
												...formData,
												source: e.target.value as SentMessageSource,
											})
										}
										validations={[Validations.required]}
										disabled={submitting}
										options={sourceOptions}
									/>
								</div>
							)}
						</FormSection>

						{showError && (
							<div className="form-message">
								<UiFieldMessage
									severity={FieldMessageSeverity.DANGER}
									label="UI_COMPONENTS.FIELD_MESSAGE.HTTP.ERROR"
								/>
							</div>
						)}

						<div className="action-buttons">
							<UiButton
								id="modalBtnCancel"
								label="UI_COMPONENTS.BUTTONS.CANCEL"
								className="p-button-outlined p-button-rounded"
								onClick={handleClose}
								disabled={submitting}
							/>
							<UiButton
								id="modalBtnSubmit"
								label={
									submitting
										? "UI_COMPONENTS.BUTTONS.SAVING"
										: "PARTICIPANT.BIOMETRICS.SENT_REPORTS.MARK_AS_SENT.SUBMIT"
								}
								className="p-button-rounded"
								type="submit"
								loading={submitting}
								disabled={submitting}
							/>
						</div>
					</form>
				)}
			/>
		</Dialog>
	);
};

export default BiometricsMarkHsrAsSent;
